/**
 * Authentication Store
 * Manages user authentication state
 */
import { defineStore } from 'pinia';
import { spaApi } from '../api/spa';
import { setDisplayTimeZone } from '../utils/kst';
import { proxyConsentApi } from '../api/proxyConsent';
import api from '../api';

// 타임아웃 래퍼 함수
function withTimeout(promise, ms) {
    const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), ms)
    );
    return Promise.race([promise, timeout]);
}

// 초기화 진행 중인지 추적 (race condition 방지)
let initializationPromise = null;

export const useAuthStore = defineStore('auth', {
    state: () => ({
        isAuthenticated: false,
        isLoading: true,
        isInitialized: false, // New flag to prevent duplicate initialization
        user: null,
        antiforgeryToken: null,
        loginConfig: null,
        error: null,
        profileImage: '', // 헤더 아바타용 프로필 이미지(base64). 사진 변경 시 갱신
        familySites: [],          // 사용자 role 매칭된 패밀리 사이트 목록
        familySitesLocale: null,  // 현재 보관 중인 사이트 응답의 locale
        familySitesLoading: false,
        familySitesRequestId: 0,  // race guard — 새 요청마다 +1, 응답 시 일치만 반영
        homeNotices: [],          // 홈 안내 문구 (NOTICE = 새 소식, PROMO = 추천 서비스 머리말)
        homeNoticesRequestId: 0,  // race guard — familySites 와 같은 이유
        // 자녀 가입 자격 (자녀 관리 메뉴 노출 게이트)
        // null = 미확인 / 로딩 중 (메뉴 숨김), true = 자격 있음, false = 자격 없음 (메뉴 숨김)
        canRegisterChildren: null,
        canRegisterChildrenRequestId: 0, // race guard — initialize 중복/순서 보장
    }),

    getters: {
        userId: (state) => state.user?.userId,
        userName: (state) => state.user?.userName,
        userEmail: (state) => state.user?.email,
        /** 이메일 인증 여부 — user 미로드 시 null (배너는 false 일 때만 표시). */
        isEmailConfirmed: (state) => (state.user ? state.user.isEmailConfirmed === true : null),
        roles: (state) => state.user?.roles || [],
        externalProviders: (state) => state.loginConfig?.externalProviders || [],
        /** STS 시스템 관리자 role (appsettings AdminConfiguration.AdministrationRole). */
        isIdentityAdminAdministrator: (state) =>
            (state.user?.roles || []).includes('IdentityAdminAdministrator'),
        /**
         * 자녀 관리 메뉴 노출 게이트. 시스템 관리자는 eligibility API 결과와 무관하게 항상 노출 —
         * API 실패 시에도 admin 은 메뉴 사용 가능 (서버 측이 최종 보호 게이트).
         * 일반 사용자는 canRegisterChildren === true 일 때만 노출.
         */
        canShowChildManagement() {
            return this.isIdentityAdminAdministrator || this.canRegisterChildren === true;
        },
    },

    actions: {
        /**
         * Initialize auth state on app load
         * Called once from main.js before app mount
         * Uses lock to prevent duplicate API calls
         */
        async initialize() {
            // 이미 초기화 완료됨
            if (this.isInitialized) {
                console.log('[AuthStore] Already initialized, skipping');
                return;
            }

            // 이미 초기화 진행 중이면 기존 Promise 반환 (중복 호출 방지)
            if (initializationPromise) {
                console.log('[AuthStore] Initialization already in progress, waiting...');
                return initializationPromise;
            }

            console.log('[AuthStore] Initializing...');

            // 초기화 Promise 저장 (다른 호출자가 기다릴 수 있도록)
            initializationPromise = (async () => {
                try {
                    // Load config, user, and antiforgery token in parallel (5초 타임아웃)
                    const [configRes, userRes, tokenRes] = await Promise.all([
                        withTimeout(spaApi.getConfig(), 5000).catch((e) => {
                            console.warn('[AuthStore] getConfig failed:', e.message);
                            return { data: null };
                        }),
                        withTimeout(spaApi.getUser(), 5000).catch((e) => {
                            console.warn('[AuthStore] getUser failed:', e.message);
                            return { data: null };
                        }),
                        withTimeout(spaApi.getAntiforgeryToken(), 5000).catch((e) => {
                            console.warn('[AuthStore] getAntiforgeryToken failed:', e.message);
                            return { data: null };
                        }),
                    ]);

                    this.loginConfig = configRes.data;
                    this.user = userRes.data;
                    setDisplayTimeZone(userRes.data?.timeZone);   // 다국가 D2: 소속 교회 시간대로 표시
                    this.isAuthenticated = !!userRes.data?.isAuthenticated;
                    this.antiforgeryToken = tokenRes.data?.token;

                    console.log('[AuthStore] Initialization complete, token:', !!this.antiforgeryToken);

                    // 자녀 관리 메뉴 게이트 — 인증된 사용자만, 백그라운드 (mount 비차단)
                    if (this.isAuthenticated) {
                        this.fetchChildRegisterEligibility();
                    } else {
                        // 미인증 — 직전 세션의 in-flight 응답이 stale 한 true 를 덮어쓰지 않도록 무효화
                        this.canRegisterChildrenRequestId++;
                        this.canRegisterChildren = false;
                    }
                } catch (error) {
                    console.error('[AuthStore] Initialization failed:', error);
                    this.error = error;
                    // Don't re-throw - let the app continue
                } finally {
                    this.isLoading = false;
                    this.isInitialized = true;
                    initializationPromise = null; // 완료 후 초기화
                }
            })();

            return initializationPromise;
        },

        /**
         * Refresh antiforgery token
         */
        async refreshToken() {
            try {
                const res = await spaApi.getAntiforgeryToken();
                this.antiforgeryToken = res.data.token;
            } catch (error) {
                console.error('Token refresh failed:', error);
            }
        },

        /**
         * Update user after login
         */
        async refreshUser() {
            try {
                const res = await spaApi.getUser();
                this.user = res.data;
                setDisplayTimeZone(res.data?.timeZone);   // 다국가 D2
                this.isAuthenticated = !!res.data?.isAuthenticated;
            } catch (error) {
                console.error('User refresh failed:', error);
            }
        },

        /**
         * 사용자 권한 매칭된 패밀리 사이트 목록 로드.
         * 인증 후 + locale 변경 시 호출.
         * race guard: 요청 시점 requestId 캡처 → 응답 도착 시 latest 와 일치할 때만 상태 반영.
         * 빠르게 locale 변경(ko→en→ja) 시 늦게 도착한 ko 응답이 ja 결과를 덮어쓰지 않도록 방지.
         */
        async loadFamilySites(lang) {
            if (!this.isAuthenticated) {
                this.familySitesRequestId++; // in-flight 응답 무효화
                this.familySites = [];
                this.familySitesLocale = null;
                this.familySitesLoading = false;
                return;
            }
            const myId = ++this.familySitesRequestId;
            this.familySitesLoading = true;
            try {
                const res = await spaApi.getFamilySites(lang || null);
                if (myId !== this.familySitesRequestId) return; // 더 새 요청이 진행 중 — 폐기
                this.familySites = res.data?.sites || [];
                this.familySitesLocale = res.data?.locale || null;
            } catch (error) {
                if (myId !== this.familySitesRequestId) return; // 폐기
                console.warn('Failed to load family sites:', error);
                this.familySites = [];
                this.familySitesLocale = null;
            } finally {
                if (myId === this.familySitesRequestId) {
                    this.familySitesLoading = false;
                }
            }
        },

        /**
         * 홈 안내 문구 로드. 홈 화면에서만 쓰므로 홈 진입 + locale 변경 시 호출한다.
         * 테이블이 비어 있거나 노출 기간이 지나면 빈 배열 — 화면은 섹션을 통째로 숨긴다.
         */
        async loadHomeNotices(lang) {
            if (!this.isAuthenticated) {
                this.homeNoticesRequestId++;
                this.homeNotices = [];
                return;
            }
            const myId = ++this.homeNoticesRequestId;
            try {
                const res = await spaApi.getHomeNotices(lang || null);
                if (myId !== this.homeNoticesRequestId) return; // 더 새 요청이 진행 중 — 폐기
                this.homeNotices = res.data?.notices || [];
            } catch (error) {
                if (myId !== this.homeNoticesRequestId) return;
                console.warn('Failed to load home notices:', error);
                this.homeNotices = [];
            }
        },

        /**
         * 헤더 아바타용 프로필 이미지 로드(얼굴사진 우선, 없으면 profile 클레임)
         * 사진 변경/삭제 후에도 호출하여 헤더를 즉시 갱신한다.
         */
        async loadProfileImage() {
            if (!this.isAuthenticated) {
                this.profileImage = '';
                return;
            }
            try {
                const res = await api.get('/api/manage/profile-image');
                // result가 true면 data(빈 문자열 포함)로 반영 → 사진 삭제 시 아바타로 폴백
                this.profileImage = res.data?.result ? (res.data.data || '') : '';
            } catch (error) {
                console.error('Failed to load profile image:', error);
            }
        },

        /**
         * Clear auth state on logout
         */
        logout() {
            this.isAuthenticated = false;
            this.user = null;
            this.profileImage = '';
            this.familySitesRequestId++; // in-flight loadFamilySites 응답 무효화
            this.homeNoticesRequestId++; // in-flight loadHomeNotices 응답 무효화
            this.homeNotices = [];
            this.familySites = [];
            this.familySitesLocale = null;
            this.familySitesLoading = false;
            // 자녀 관리 게이트 — 다른 계정 stale state 방지
            this.canRegisterChildrenRequestId++;
            this.canRegisterChildren = false;
        },

        /**
         * 자녀 관리 자격 확인 — fire-and-forget (백그라운드).
         * - 시스템 관리자는 서버에서 항상 eligible=true 반환 (reason='ADMIN').
         * - 호출 실패 / 모호한 응답 → fail-closed (canRegisterChildren=false).
         * - race guard: 동시 호출 / logout 직후 응답이 stale 인 경우 무시.
         */
        async fetchChildRegisterEligibility() {
            const reqId = ++this.canRegisterChildrenRequestId;
            try {
                const resp = await withTimeout(
                    proxyConsentApi.checkChildRegisterEligibility(),
                    5000,
                );
                if (reqId !== this.canRegisterChildrenRequestId) {
                    // logout 또는 후속 호출로 인해 무효화 — 무시
                    return;
                }
                const body = resp?.data;
                const ok = body?.success === true && body?.data?.eligible === true;
                this.canRegisterChildren = ok;
            } catch (e) {
                if (reqId !== this.canRegisterChildrenRequestId) return;
                console.warn('[AuthStore] fetchChildRegisterEligibility failed:', e?.message);
                this.canRegisterChildren = false;
            }
        },

        /**
         * Reset store state
         */
        reset() {
            this.$reset();
        },
    },
});
